import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight, FiArrowUp, FiArrowUpRight, FiCode,
  FiFileText, FiGrid, FiHome, FiImage, FiPlay, FiSettings,
  FiShoppingBag, FiTrendingUp, FiVideo,
} from "react-icons/fi";
import { HiOutlineFire, HiOutlineSparkles } from "react-icons/hi2";
import type { IconType } from "react-icons";
import type { Item } from "./App";

const asset = (name: string) => `/images/storefront-v1/${name}.webp`;
const categoryIcons: Record<string, IconType> = {
  featured: HiOutlineFire, visual: FiImage, video: FiVideo,
  avatar: FiPlay, copywriting: FiFileText, analytics: FiTrendingUp,
  "supply-chain": FiShoppingBag, efficiency: FiSettings,
};
function Icon({ icon, className }: { icon: IconType; className?: string }) {
  const Component = icon as (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  return <Component aria-hidden="true" className={className} />;
}
function Artwork({ name, alt = "", className = "", eager = false }: {
  name: string; alt?: string; className?: string; eager?: boolean;
}) {
  return <img className={className} src={asset(name)} alt={alt}
    width={510} height={340} loading={eager ? "eager" : "lazy"} decoding="async" />;
}

const features = [
  { image: "product", title: "AI 商品主图生成", label: "爆款必备", tone: "orange", description: "AI 创作高质感商品主图，多风格呈现每一处细节。" },
  { image: "video", title: "商品视频制作", label: "视觉营销", tone: "cyan", description: "从商品展示到短视频，让好产品被更多人看见。" },
  { image: "copywriting", title: "营销文案生成", label: "营销利器", tone: "pink", description: "提炼卖点、撰写文案，让每一次表达更有吸引力。" },
  { image: "analytics", title: "选品数据分析", label: "数据分析", tone: "violet", description: "洞察市场趋势与商品表现，为选品找到更多灵感。" },
  { image: "livestream", title: "直播脚本生成", label: "直播助手", tone: "rose", description: "梳理直播话术与产品讲解，让内容创作更从容。" },
];
const tutorials = [
  { title: "AI 电商实战指南", description: "工具选择与基础工作流程", category: "入门指南" },
  { title: "商品主图设计技巧", description: "构图、背景与商品细节", category: "商品视觉" },
  { title: "短视频带货全攻略", description: "选题、拍摄与剪辑思路", category: "视频创作" },
  { title: "选品数据分析教程", description: "市场趋势与商品表现", category: "选品运营" },
  { title: "直播带货话术模板", description: "卖点梳理与产品讲解", category: "直播营销" },
];
const tasks = [
  { title: "做商品图", slug: "visual" },
  { title: "剪商品视频", slug: "video" },
  { title: "写营销文案", slug: "copywriting" },
  { title: "找选品工具", slug: "analytics" },
];

function SectionTitle({ title, subtitle, icon, href, id }: {
  title: string; subtitle: string; icon: IconType; href: string; id?: string;
}) {
  return <div className="store-section-heading">
    <h2 id={id}><Icon icon={icon} />{title}</h2>
    <p>{subtitle}</p>
    <Link to={href}>查看更多 <Icon icon={FiArrowRight} /></Link>
  </div>;
}

export default function Storefront({ page, categories, search, children }: {
  page: Item; categories: Item[]; search: ReactNode; children: ReactNode;
}) {
  const title = page.hero_title || page.name;
  const [activeSection, setActiveSection] = useState("store-home");
  const [showTop, setShowTop] = useState(false);
  const sidebar = useRef<HTMLElement>(null);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      const headerHeight = document.querySelector("header.navbar")?.getBoundingClientRect().height || 64;
      const mobile = window.matchMedia("(max-width: 720px)").matches;
      const offset = headerHeight + (mobile ? sidebar.current?.offsetHeight || 0 : 0) + 28;
      let active = "store-home";
      for (const category of categories) {
        const id = `category-${category.id}`;
        const section = document.getElementById(id);
        if (section && section.getBoundingClientRect().top <= offset) active = id;
      }
      if (window.scrollY > 0 && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2 && categories.length) {
        active = `category-${categories[categories.length - 1].id}`;
      }
      setActiveSection(active);
      setShowTop(window.scrollY > 600);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [categories]);
  useEffect(() => {
    const nav = sidebar.current;
    const active = nav?.querySelector<HTMLElement>("[aria-current='location']");
    if (!nav || !active) return;
    // Reveal the selected mobile tab without scrolling the document itself.
    const item = active.getBoundingClientRect();
    const bounds = nav.getBoundingClientRect();
    if (item.left < bounds.left || item.right > bounds.right) {
      nav.scrollLeft += item.left - bounds.left - (bounds.width - item.width) / 2;
    }
  }, [activeSection]);
  return <div className="storefront" id="store-home" data-page-ready="true">
    <section className="hero store-hero" aria-labelledby="store-hero-title">
      <div className="store-hero-inner">
        <div className="store-hero-copy">
          <span className="store-eyebrow">电商工具导航</span>
          <h1 id="store-hero-title">{title}</h1>
          <p>{page.hero_subtitle || "商品做图、视频、文案与运营，找到适合当前任务的工具。"}</p>
        </div>
        <div className="store-hero-find">
          {search}
          <nav className="store-quicklinks" aria-label="常用场景">
            {tasks.map(task => <Link key={task.slug} to={`/category/${task.slug}`}>
              <span>{task.title}</span><Icon icon={FiArrowUpRight} />
            </Link>)}
          </nav>
        </div>
      </div>
    </section>

    <div className="store-layout">
      <aside ref={sidebar} className="store-sidebar" aria-label="电商场景导航">
        <Link className="store-sidebar-home" to="#store-home" aria-current={activeSection === "store-home" ? "location" : undefined}><Icon icon={FiHome} /><b>首页</b></Link>
        {categories.map(c => <Link className="store-sidebar-link" to={`#category-${c.id}`} key={c.id}
          aria-current={activeSection === `category-${c.id}` ? "location" : undefined}>
          <Icon icon={categoryIcons[c.slug || ""] || FiGrid} />
          <b>{c.name}</b><span>{c.websites?.total || 0}</span>
        </Link>)}
        <Link className="store-sidebar-link" to="/news"><Icon icon={FiFileText} /><b>行业快讯</b></Link>
        <Link className="store-sidebar-link" to="/category/llm-api"><Icon icon={FiCode} /><b>编程接口</b><small>API</small></Link>
        <div className="store-sidebar-footer"><Icon icon={HiOutlineSparkles} /><p>为电商创作者而生</p><span>让灵感落地，让生意生长。</span><Link to="/submit">分享一个好工具 <Icon icon={FiArrowUpRight} /></Link></div>
      </aside>

      <div className="store-main">
        <section className="store-promotions" aria-label="电商创作灵感">
          <article className="store-campaign" data-display-only="true">
            <div className="store-campaign-copy"><small>商品视觉</small><h2>好产品，从看清细节开始。</h2><p>主图、场景图与详情页，呈现产品本身。</p></div>
          </article>
          <article className="store-mini-promo store-new" data-display-only="true">
            <div><small>上新准备</small><h3>把卖点说清楚</h3><p>商品标题 · 详情文案 · 推广内容</p></div>
          </article>
          <article className="store-mini-promo store-live" data-display-only="true">
            <div><small>直播内容</small><h3>让讲解有条理</h3><p>产品介绍 · 讲解顺序 · 互动话术</p></div>
          </article>
        </section>

        <section className="store-feature-section" id="store-recommendations" aria-labelledby="store-recommendations-title">
          <SectionTitle title="热门推荐" subtitle="精选电商创作场景，发现更多实用灵感" icon={HiOutlineFire} href="/category/featured" id="store-recommendations-title" />
          <div className="store-feature-grid">
            {features.map(feature => <article className={`store-feature ${feature.tone}`} key={feature.image} data-display-only="true">
              <div className="store-feature-art"><Artwork name={feature.image} eager /><span>{feature.label}</span></div>
              <div className="store-feature-copy"><h3>{feature.title}</h3><p>{feature.description}</p><button type="button" disabled>敬请期待 <Icon icon={FiArrowRight} /></button></div>
            </article>)}
          </div>
        </section>

        <section className="store-scenarios" aria-labelledby="store-scenarios-title">
          <SectionTitle title="电商工具分类" subtitle="覆盖电商创作与运营，找到适合你的工具" icon={HiOutlineFire} href="#tool-directory" id="store-scenarios-title" />
          <div className="store-scenario-grid">
            {categories.map((c, i) => <Link key={c.id} to={`/category/${c.slug}`} className={`store-scenario scenario-${i % 8}`}>
              <span><Icon icon={categoryIcons[c.slug || ""] || FiGrid} /></span><b>{c.name}</b>
            </Link>)}
          </div>
        </section>

        <section className="store-tutorials" aria-labelledby="store-tutorials-title">
          <SectionTitle title="实战教程" subtitle="围绕具体任务，整理可复用的工作方法" icon={FiVideo} href="/tutorials" id="store-tutorials-title" />
          <div className="store-tutorial-grid">
            {tutorials.map((tutorial, index) => <article className="store-tutorial" key={tutorial.title} data-display-only="true">
              <div className="store-tutorial-meta"><span>{tutorial.category}</span><span aria-hidden="true">0{index + 1}</span></div>
              <h3>{tutorial.title}</h3><p>{tutorial.description}</p>
              <small>整理中</small>
            </article>)}
          </div>
        </section>

        <div className="store-tool-directory" id="tool-directory">
          <SectionTitle title="工具导航" subtitle="按业务场景，直达你需要的网站与工具" icon={FiGrid} href="/new" />
          {children}
        </div>
      </div>
    </div>
    {showTop && <Link className="back-to-top" to="#store-home" aria-label="返回顶部"><Icon icon={FiArrowUp} /></Link>}
  </div>;
}
